// ── ALDO Custom Autocomplete — Staging ────────────────────────────────────
// Keeps app collections + query suggestions hooks.
// Replaces product results with a custom Algolia query (distinct: true).

const ALGOLIA_APP_ID   = 'L5DMAZJAQR';
const ALGOLIA_API_KEY  = '0dcacb559b62d6137cedbb72271e45ce';
const ALGOLIA_INDEX    = 'aldo_shoes_shopify_bahrain_prodproducts';
const PRODUCTS_TO_SHOW = 4;
const HITS_TO_FETCH    = 20;

// ── Direct Algolia search (distinct: true) ────────────────────────────────
async function searchAlgoliaProducts(query, lang) {
  const filters = 'inventory_quantity > 0';
  const body = {
    requests: [{
      indexName: ALGOLIA_INDEX,
      query: query,
      hitsPerPage: HITS_TO_FETCH,
      distinct: true,
      filters: filters,
      clickAnalytics: true
    }]
  };

  const res = await fetch(
    `https://${ALGOLIA_APP_ID}-dsn.algolia.net/1/indexes/*/queries`,
    {
      method: 'POST',
      headers: {
        'X-Algolia-Application-Id': ALGOLIA_APP_ID,
        'X-Algolia-API-Key': ALGOLIA_API_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    }
  );

  const data = await res.json();
  const hits = data?.results?.[0]?.hits || [];

  // Deduplicate by color_key client-side as safety net
  const seen = new Set();
  return hits.filter(hit => {
    const key = hit?.meta?.custom?.color_key || hit.objectID;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).slice(0, PRODUCTS_TO_SHOW);
}

// ── Swatch extractor ──────────────────────────────────────────────────────
function extractSwatchesCustom(data) {
  if (!data) return [];
  if (typeof data === 'string') {
    try { data = JSON.parse(data); } catch (e) { return []; }
  }
  if (!data.swatches || !Array.isArray(data.swatches)) return [];
  const seen = new Set();
  return data.swatches.filter(s => {
    if (!s.color || !s.shopifyImageUrl) return false;
    if (seen.has(s.color)) return false;
    seen.add(s.color);
    return true;
  }).map(s => ({ color: s.color, shopifyImageUrl: s.shopifyImageUrl }));
}

// ── Build product card HTML ───────────────────────────────────────────────
function buildProductCard(hit, lang) {
  const isArabic = lang === 'ar';
  const fallback = 'https://cdn.shopify.com/s/files/1/0721/9960/2220/files/Content_f84bf220-58e1-47f2-a3f1-0fd9040b65e8.png?v=1779089106';
  const img = hit.image || '';
  const image = (!img || img.toLowerCase().includes('inputdataempty') || img.toLowerCase().includes('inputdataemtpy'))
    ? fallback : img;

  const customMeta = hit?.meta?.custom || {};
  const title = isArabic ? (customMeta.arabic_title || hit.title) : hit.title;
  const silhouette = isArabic ? (customMeta.silhouette_ar || '') : (customMeta.silhouette || '');
  const link = isArabic ? `/ar/products/${hit.handle}` : `/products/${hit.handle}`;

  const metaDiscount = parseFloat(customMeta.discount_percentage || 0);
  let currentPrice = hit.price || 0;
  let originalPrice = (hit.compare_at_price > hit.price) ? hit.compare_at_price : hit.price;
  let isOnSale = false;

  if (metaDiscount > 0) {
    originalPrice = hit.price;
    currentPrice = hit.price * (100 - metaDiscount) / 100;
    isOnSale = true;
  } else if (hit.compare_at_price > hit.price) {
    isOnSale = true;
  }

  const priceText = currentPrice.toFixed(3) + ' BHD';
  const compareText = isOnSale ? originalPrice.toFixed(3) + ' BHD' : '';

  const swatches = extractSwatchesCustom(customMeta.color_swatch_map);
  const swatchesHtml = swatches.length ? `
    <div class="aldo-color-swatches__grid">
      ${swatches.map((s, i) => `
        <div class="aldo-color-swatches__item${i === 0 ? ' active' : ''}"
          data-color="${s.color}"
          data-image="${s.shopifyImageUrl}">
          <img src="${s.shopifyImageUrl}" alt="${s.color}" width="28" height="28"/>
        </div>
      `).join('')}
    </div>` : '';

  const uniqueId = `custom-product-${hit.objectID}`;

  return `
    <div id="${uniqueId}" class="card-wrapper product-card-wrapper custom-product-card-inner"
      data-product-handle="${hit.handle}" data-option1="${hit.option1 || ''}">
      <div class="card card--standard card--media">
        <div class="card__inner color-scheme-2 gradient ratio" style="--ratio-percent:100%">
          <div class="card__media">
            <div class="media media--transparent media--hover-effect">
              <img class="motion-reduce aldo-primary-image"
                src="${image}" srcset="${image}" alt="${hit.title}"
                loading="lazy" width="533"/>
            </div>
          </div>
        </div>
        <div class="card__content bg-grid-color">
          <div class="card__information">
            <h3 class="card__heading">
              <a class="full-unstyled-link custom-product-title aldo-product-link"
                href="${link}">
                ${title}${silhouette ? ' - ' + silhouette : ''}
              </a>
            </h3>
            <div class="price">
              <b>${priceText}</b>
              <span class="price-strike">${compareText}</span>
            </div>
            ${swatchesHtml}
          </div>
        </div>
      </div>
    </div>`;
}

// ── Wire swatches + fetch variants for a card ─────────────────────────────
function wireCard(card, handle, option1, inventoryAvailable) {
  const swatchItems = Array.from(card.querySelectorAll('.aldo-color-swatches__item'));
  const primaryImg  = card.querySelector('.aldo-primary-image');
  const productLink = card.querySelector('.aldo-product-link');
  const isArabic    = document.documentElement.lang === 'ar';

  if (!swatchItems.length) return;

  // Immediately activate correct swatch by option1
  const hitColor = (option1 || '').toLowerCase().trim();
  const targetSwatch = swatchItems.find(
    s => (s.dataset.color || '').toLowerCase().trim() === hitColor
  ) || swatchItems[0];

  swatchItems.forEach(s => s.classList.remove('active'));
  targetSwatch.classList.add('active');

  // Immediately set data-available on indexed color swatch from hit data
  // so +N and availability show without waiting for fetch
  if (targetSwatch) {
    targetSwatch.dataset.available = inventoryAvailable ? 'true' : 'false';
  }

  // Prevent mousedown on swatch grid from closing panel
  const grid = card.querySelector('.aldo-color-swatches__grid');
  if (grid) {
    grid.addEventListener('mousedown', e => {
      e.stopPropagation();
      e.preventDefault();
      document.querySelector('.aa-Input')?.focus();
    });
  }

  // ── FETCH CACHE — reuse variant data if already fetched ──
  if (!window._aldoVariantCache) window._aldoVariantCache = {};

  const processData = (data) => {
      if (!card.isConnected) return;

      const variants = data.variants;
      const select = document.createElement('select');
      select.className = 'aldo-variant-select';
      select.style.display = 'none';

      variants.forEach(v => {
        const opt = document.createElement('option');
        opt.value = v.id;
        opt.setAttribute('data-option1', v.option1 || '');
        opt.setAttribute('data-option2', v.option2 || '');
        opt.setAttribute('data-image', v.featured_image ? v.featured_image.src : '');
        opt.setAttribute('data-available', v.available ? 'true' : 'false');
        select.appendChild(opt);
      });
      card.querySelector('.aldo-variant-select')?.remove();
      card.appendChild(select);

      // Wire each swatch to its variant
      swatchItems.forEach(swatch => {
        const color = (swatch.dataset.color || '').toLowerCase().trim();
        const colorOpts = Array.from(select.options).filter(o =>
          (o.dataset.option1 || '').toLowerCase().trim() === color ||
          (o.dataset.option2 || '').toLowerCase().trim() === color
        );
        if (!colorOpts.length) return;
        const chosen = colorOpts.find(o => o.dataset.available === 'true') || colorOpts[0];
        swatch.dataset.variantId = chosen.value;
        swatch.dataset.available = chosen.dataset.available;
      });

      // ── +N overflow: show max 4 available swatches, hide the rest ──
      const swatchGrid = card.querySelector('.aldo-color-swatches__grid');
      if (swatchGrid) {
        swatchGrid.querySelector('.aldo-color-swatches__more')?.remove();
        const availableSwatches = swatchItems.filter(s => s.dataset.available === 'true');
        const maxVisible = 4;
        const extraCount = availableSwatches.length - maxVisible;
        availableSwatches.forEach((s, i) => {
          s.style.display = i >= maxVisible ? 'none' : '';
        });
        if (extraCount > 0) {
          const more = document.createElement('span');
          more.className = 'aldo-color-swatches__more';
          more.textContent = `+${extraCount}`;
          swatchGrid.appendChild(more);
        }
      }

      // Update active swatch image + link using real variant image
      const activeNow = card.querySelector('.aldo-color-swatches__item.active');
      if (activeNow) {
        const variantId = activeNow.dataset.variantId;
        const matchedOpt = variantId
          ? Array.from(select.options).find(o => o.value === variantId)
          : null;
        const variantImage = matchedOpt?.getAttribute('data-image') || null;
        if (primaryImg && variantImage) {
          primaryImg.src = variantImage;
          primaryImg.srcset = variantImage;
        }
        if (productLink && variantId) {
          productLink.href = isArabic
            ? `/ar/products/${handle}?variant=${variantId}`
            : `/products/${handle}?variant=${variantId}`;
        }
      }

      // Wire swatch clicks
      swatchItems.forEach(swatchEl => {
        swatchEl.addEventListener('mousedown', e => {
          e.stopPropagation();
          e.preventDefault();
          document.querySelector('.aa-Input')?.focus();
        });

        swatchEl.addEventListener('click', e => {
          e.stopPropagation();
          e.preventDefault();
          document.querySelector('.aa-Input')?.focus();

          swatchItems.forEach(s => s.classList.remove('active'));
          swatchEl.classList.add('active');

          const variantId = swatchEl.dataset.variantId;
          const matched = variantId
            ? Array.from(select.options).find(o => o.value === variantId)
            : null;
          const variantImage = matched?.getAttribute('data-image') || swatchEl.dataset.image;

          if (primaryImg && variantImage) {
            primaryImg.src = variantImage;
            primaryImg.srcset = variantImage;
          }
          if (productLink && variantId) {
            productLink.href = isArabic
              ? `/ar/products/${handle}?variant=${variantId}`
              : `/products/${handle}?variant=${variantId}`;
          }
        });
      });
  }; // end processData

  if (window._aldoVariantCache[handle]) {
    processData(window._aldoVariantCache[handle]);
  } else {
    fetch(`/products/${handle}.js`)
      .then(r => r.json())
      .then(data => {
        window._aldoVariantCache[handle] = data;
        processData(data);
      })
      .catch(e => console.error(`Variant fetch error for ${handle}:`, e));
  }
}

// ── Inject custom products into the aa-Panel ──────────────────────────────
let lastQuery = '';
let injectTimer = null;

function injectCustomProducts(query) {
  const lang = document.documentElement.lang || 'en';

  searchAlgoliaProducts(query, lang).then(hits => {
    // Find the products source container in the aa-Panel
    const productsSource = document.querySelector(
      '.aa-Panel .aa-Source[data-autocomplete-source-id="products"] .aa-List'
    );
    if (!productsSource) return;

    // Clear existing items
    productsSource.innerHTML = '';

    hits.forEach(hit => {
      const li = document.createElement('li');
      li.className = 'aa-Item';
      li.setAttribute('role', 'option');
      li.setAttribute('aria-selected', 'false');
      li.innerHTML = buildProductCard(hit, lang);
      productsSource.appendChild(li);

      // Wire swatches after DOM insertion
      const card = li.querySelector('.custom-product-card-inner');
      if (card) {
        const option1 = card.dataset.option1;
        wireCard(card, hit.handle, option1, hit.inventory_available);
      }
    });
  });
}

// ── Watch search input and trigger custom injection ───────────────────────
document.addEventListener('DOMContentLoaded', () => {
  const observer = new MutationObserver(() => {
    const input = document.querySelector('.aa-Input');
    if (!input || input.dataset.customProductsBound) return;
    input.dataset.customProductsBound = 'true';

    input.addEventListener('input', () => {
      const query = input.value.trim();
      clearTimeout(injectTimer);
      if (!query) return;
      injectTimer = setTimeout(() => {
        injectCustomProducts(query);
      }, 200);
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
});

// ── Also re-inject when aa-Panel renders (MutationObserver on panel) ──────
document.addEventListener('DOMContentLoaded', () => {
  const panelObserver = new MutationObserver(() => {
    const input = document.querySelector('.aa-Input');
    const query = input?.value?.trim() || '';
    if (!query || query === lastQuery) return;
    lastQuery = query;
    injectCustomProducts(query);
  });

  const checkPanel = setInterval(() => {
    const panel = document.querySelector('.aa-Panel');
    if (panel) {
      clearInterval(checkPanel);
      panelObserver.observe(panel, { childList: true, subtree: true });
    }
  }, 300);
});


// ── Keep existing app hooks (collections + query suggestions) ─────────────

document.addEventListener('algolia.hooks.initialize', function () {

  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteConfigurationOptions',
    function (defaultOptions) {
      return Object.assign({}, defaultOptions, {
        shouldPanelOpen: ({ state }) => state.query.length > 0
      });
    }
  );

  algoliaShopify.hooks.registerHook(
    'beforeAutocompleteCollectionsTemplate',
    function (defaultTemplate, templateOptions) {
      const { html, item } = templateOptions;
      const isArabic = document.documentElement.lang === 'ar';
      const collectionTitle = item?.meta?.custom?.arabic_title || item.title;
      const link = isArabic
        ? `/ar/collections/${item.handle}`
        : `/collections/${item.handle}`;
      return html`
        <a href="${link}" class="aa-ItemLink">
          <div class="aa-ItemContent">
            <div class="aa-ItemContentTitle">
              ${isArabic ? collectionTitle : item.title}
            </div>
          </div>
        </a>`;
    }
  );
});
